import { FieldLabel, HeadlessUI } from '@systemeio/ui'
import { NotificationMenuMobile } from 'modules/notifications/components/notifications-menu-mobile'
import { useRouter } from 'next/router'
import React, { Fragment, useEffect } from 'react'
import SideMenuItem from 'shared/components/header/components/side-menu/components/side-menu-item'
import WorkspaceSelector from 'shared/components/header/components/workspace-selector'
import { useMenuItems } from 'shared/components/header/hooks/use-menu-items'
import { MobileLanguageSwitcher } from 'shared/components/language-switcher/mobile-language-switcher'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { ScreenHeightDiv } from 'shared/components/screen-height-div'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { SubAccountSelector } from '../sub-account-selector'
import { ArrowLeftIcon, BurgerIcon, LogoutIcon, OpenInNewIcon } from '@systemeio/ui'
import { hasSubAccounts } from 'shared/hooks/use-user/utils/has-subaccounts'

interface SideMenuProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const SideMenu = ({ isOpen, setIsOpen }: SideMenuProps) => {
  const {
    isWorkspaceOwner,
    user,
    userStatus,
    checkAdminRoles,
    isLoading,
    isDisabled,
    isSupportImpersonation,
    shouldShowSubAccountPage,
    avatarUrl,
  } = useUser()
  const { getMenuItems, upgradeAccountItem } = useMenuItems()
  const { t } = useLocoTranslation()
  const { pathname } = useRouter()

  const isSettingsPage = pathname.includes('/profile')
  const isSubaccountPage = pathname.includes('/subaccounts')
  const hasWorkspaces =
    user?.workspaceMemberships &&
    Array.isArray(user.workspaceMemberships) &&
    user.workspaceMemberships.length > 0

  useEffect(() => {
    setIsOpen(false)
  }, [pathname, setIsOpen])

  return (
    <div className={`${isLoading && 'animate-pulse'} flex w-full items-center justify-end gap-5`}>
      <NotificationMenuMobile />
      <button disabled={isLoading} aria-label="Open Menu" onClick={() => setIsOpen(true)}>
        <BurgerIcon className="h-8 w-8" />
      </button>
      <HeadlessUI.Transition show={isOpen} as={Fragment}>
        <HeadlessUI.Dialog className="fixed inset-0 z-10" onClose={() => setIsOpen(false)}>
          <HeadlessUI.TransitionChild
            as={Fragment}
            enter="ease duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <HeadlessUI.DialogBackdrop className="fixed inset-0 bg-black/50" />
          </HeadlessUI.TransitionChild>

          <HeadlessUI.TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-x-full"
            enterTo="opacity-100 translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 translate-x-full"
          >
            <HeadlessUI.DialogPanel>
              <ScreenHeightDiv className="bg-background-secondary text-text-primary fixed right-0 top-0 z-10 flex min-w-[17rem] max-w-[350px]  flex-col overflow-auto px-4 py-5">
                <div
                  onClick={() => setIsOpen(false)}
                  className="flex w-fit cursor-pointer items-center gap-2"
                >
                  <ArrowLeftIcon className="stroke-text-primary" />
                  <span>{t('global.back_to_page')}</span>
                </div>
                {!isDisabled && user && (
                  <div className="flex flex-col gap-3.5 py-6">
                    <div className="flex justify-between gap-3.5">
                      {isWorkspaceOwner ? (
                        <div className="flex items-center gap-3.5">
                          <img
                            className="h-10 w-10 overflow-hidden rounded-full object-cover"
                            src={avatarUrl}
                            alt={`${user.firstName} ${user.lastName}`}
                          />
                          <div className="flex flex-col">
                            <span className="text-base font-semibold">
                              {user.firstName} {user.lastName}
                            </span>
                            <LinkWithoutPrefetch
                              href={'/profile/user-settings'}
                              className={isSettingsPage ? 'text-brand-primary' : ''}
                            >
                              {t('global.settings')}
                            </LinkWithoutPrefetch>
                            {!hasSubAccounts(user) && shouldShowSubAccountPage && (
                              <LinkWithoutPrefetch
                                className={isSubaccountPage ? 'text-brand-primary' : ''}
                                href={'/subaccounts'}
                              >
                                {t('header.user.profile.menu.sub_accounts')}
                              </LinkWithoutPrefetch>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3.5">
                          <img
                            className="h-10 w-10 overflow-hidden rounded-full object-cover"
                            src={avatarUrl}
                            alt={`${user.firstName} ${user.lastName}`}
                          />
                          <div className="flex flex-col">
                            <span className="text-base font-semibold">
                              {user.firstName} {user.lastName}
                            </span>
                            <LinkWithoutPrefetch
                              href={'/profile/account-settings'}
                              className={isSettingsPage ? 'text-brand-primary' : ''}
                            >
                              {t('global.settings')}
                            </LinkWithoutPrefetch>
                            {!hasSubAccounts(user) && shouldShowSubAccountPage && (
                              <LinkWithoutPrefetch
                                className={isSubaccountPage ? 'text-brand-primary' : ''}
                                href={'/subaccounts'}
                              >
                                {t('header.user.profile.menu.sub_accounts')}
                              </LinkWithoutPrefetch>
                            )}
                          </div>
                        </div>
                      )}
                      <MobileLanguageSwitcher />
                    </div>
                    {hasSubAccounts(user) && (
                      <div className="flex flex-col">
                        <FieldLabel
                          labelClassName={'mb-1'}
                          label={
                            <div className={'flex gap-1.5'}>
                              {t('header.user.profile.menu.sub_accounts')}
                              {shouldShowSubAccountPage && (
                                <LinkWithoutPrefetch
                                  href={'/subaccounts'}
                                  className={`group self-center outline-hidden`}
                                >
                                  <OpenInNewIcon
                                    className={
                                      'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
                                    }
                                  />
                                </LinkWithoutPrefetch>
                              )}
                            </div>
                          }
                        />
                        <SubAccountSelector />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col gap-8 py-4">
                  {user && !isDisabled && (
                    <>
                      {user.userPlanId === 40 && isWorkspaceOwner && !isSupportImpersonation && (
                        <SideMenuItem item={upgradeAccountItem} />
                      )}
                      {getMenuItems(userStatus).map((item, idx) => {
                        if (item.item.isAdmin && !checkAdminRoles()) {
                          return
                        }
                        return <SideMenuItem key={idx} item={item.item} />
                      })}
                    </>
                  )}
                </div>
                <div className="mt-auto flex flex-col gap-3">
                  <div>
                    {hasWorkspaces && (
                      <FieldLabel
                        labelClassName={'mb-1'}
                        label={t('header.user.profile.menu.workspace')}
                      />
                    )}
                    <WorkspaceSelector
                      afterChange={() => setIsOpen(false)}
                      className={'text-text-primary'}
                    />
                  </div>
                  {isSupportImpersonation && (
                    <a href={`/dashboard/back/entry?pretend_this_user=_exit`}>
                      <p className="cursor-pointer px-2">{t('header.exit_impersonation')}</p>
                    </a>
                  )}
                  <div className="flex items-center">
                    <SideMenuItem
                      item={{
                        mobileIconRenderer: () => <LogoutIcon />,
                        href: `/logout`,
                        name: t('header.user.profile.menu.logout'),
                      }}
                    />
                  </div>
                </div>
              </ScreenHeightDiv>
            </HeadlessUI.DialogPanel>
          </HeadlessUI.TransitionChild>
        </HeadlessUI.Dialog>
      </HeadlessUI.Transition>
    </div>
  )
}

export default SideMenu
